import './painting-flow-item-edit.css';

import { IonLabel } from '@ionic/react';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { ProgramState } from '../../../../../../components/machine/machine-state';
import {
  isProgramState,
  isProgramStopped,
} from '../../../../../../components/machine/machine.utils';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../../pages/machine/machine-context-provider';
import { FlowItemCopyAction } from '../../../../shared/component/flow-item/action/flow-item-copy-action';
import { FlowItemDeleteAction } from '../../../../shared/component/flow-item/action/flow-item-delete-action';
import { FlowItemMoveHereAction } from '../../../../shared/component/flow-item/action/flow-item-move-here-action';
import { FlowItemShiftAction } from '../../../../shared/component/flow-item/action/flow-item-shift-action';
import { FlowItemStartAtAction } from '../../../../shared/component/flow-item/action/flow-item-start-at-action';
import { FlowItemTouchUpPointAction } from '../../../../shared/component/flow-item/action/flow-item-touch-up-point-action';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { ExecutionInfo } from '../../../../shared/hook/live-execution.hooks';
import { FlowItem } from '../../../../shared/model/flow.types';
import { useHasBeaconPaintingFieldPermission } from '../../../../shared/util/permission';
import { BeaconPaintingData } from '../../model/beacon-painting-data';

export interface PaintingFlowItemEditActionBarProps {
  items: FlowItem[];
  readOnly?: boolean;
  setStartAtId?: (id: string, enableStepMode?: boolean) => Promise<void>;
  startAtId?: string;
  executionInfo?: ExecutionInfo;
  onDelete: (items: FlowItem[]) => Promise<void>;
}

const PaintingFlowItemEditActionBar = (
  props: PaintingFlowItemEditActionBarProps
): ReactElement | null => {
  const { items, readOnly, setStartAtId, startAtId, executionInfo, onDelete } =
    props;

  const { machine } = useMachineContext();
  const { flow, flowAnalyticService } =
    useFlowServices<BeaconPaintingData.FlowConfig>();

  let item: FlowItem | undefined = undefined;
  if (items[0]) {
    item = items[0];
  }
  const hasMultipleItems = items.length > 1;
  const canSetStartAt = useHasBeaconPaintingFieldPermission(
    machine?.id,
    'startAt'
  );

  const actionsDisabled = useMemo(() => {
    if (machine) {
      return isProgramState(machine, [
        ProgramState.PROGRAM_PLAYING,
        ProgramState.PROGRAM_PAUSED,
      ]);
    }

    return false;
  }, [machine]);

  const [isSettingStart, setIsSettingStart] = useState<boolean>(false);

  const onSetStartChange = useCallback(
    async (enableStepMode?: boolean) => {
      if (items.length === 1) {
        const item = items[0];
        if (typeof setStartAtId === 'function' && item.id) {
          setIsSettingStart(true);
          await setStartAtId(item.id, enableStepMode);
          setIsSettingStart(false);
        }
      }
    },
    [items, setStartAtId]
  );

  const isStepMode = flow.config.stepModeEnabled;

  const buttonsDisabled = actionsDisabled || readOnly || hasMultipleItems;

  let startHereDisabled = hasMultipleItems;

  if (actionsDisabled || isSettingStart) {
    startHereDisabled = true;
  } else {
    if (readOnly && !canSetStartAt) {
      startHereDisabled = true;
    }
  }

  const isStopped = isProgramStopped(
    machine?.state?.transient?.status?.programState
  );

  const captureWaypointDisabled =
    (buttonsDisabled && !isStepMode) ||
    (isStepMode && executionInfo === undefined && !isStopped);

  const canTouchUp =
    !hasMultipleItems && item !== undefined
      ? [
          BeaconPaintingData.FlowItemRef.PAINT,
          BeaconPaintingData.FlowItemRef.MOVE,
        ].includes(item.ref as BeaconPaintingData.FlowItemRef)
      : false;
  const canMoveHere =
    !hasMultipleItems && item !== undefined
      ? canTouchUp ||
        [BeaconPaintingData.FlowItemRef.MOVE_HOME].includes(
          item.ref as BeaconPaintingData.FlowItemRef
        )
      : false;
  const canDelete = !readOnly;

  const actionsDisabledClass = ['flow-item-actions-disabled'];
  if (actionsDisabled) {
    actionsDisabledClass.push('visible');
  }

  const canShiftItems = hasMultipleItems;

  return (
    <>
      <IonLabel className={actionsDisabledClass.join(' ')}>
        {getLocalizedText('common:actions_disabled')}
      </IonLabel>
      <div
        style={{
          margin: '16px 0px',
          display: 'flex',
        }}
      >
        {item && (
          <FlowItemTouchUpPointAction
            item={item}
            disabled={!canTouchUp || captureWaypointDisabled}
            stopProgramFirst={false}
          />
        )}
        <FlowItemMoveHereAction
          item={item}
          disabled={!canMoveHere || buttonsDisabled}
        />
        {canShiftItems ? (
          <FlowItemShiftAction disabled={readOnly} items={items} />
        ) : (
          <FlowItemStartAtAction
            item={item}
            onSetStartChange={onSetStartChange}
            startAtId={startAtId}
            disabled={isSettingStart || startHereDisabled}
          />
        )}
        <FlowItemCopyAction
          items={items}
          onCopy={() => {
            flowAnalyticService.trackCopyFlowItems();
          }}
        />
        <FlowItemDeleteAction
          disabled={!canDelete}
          onClick={() => {
            if (onDelete) {
              onDelete(items);
            }
          }}
        />
      </div>
    </>
  );
};

export { PaintingFlowItemEditActionBar };
